import {
  AccountingSoftware,
  Address,
  ApiKey,
  BankAccount,
  Brand,
  Category,
  Company,
  CompanyOfficer,
  Customer,
  CustomerAddress,
  DocumentAttachment,
  EntityCustomField,
  ExternalIntegration,
  ImageAttachment,
  InventoryOrder,
  InventoryOrderProduct,
  Invoice,
  InvoiceReminder,
  Location,
  CompanyFullData,
  ManagedCompanyIntegration,
  Note,
  Order,
  OrderProduct,
  PayeeAccount,
  Payment,
  PaymentProviders,
  PersonEntityType,
  PickupPoint,
  Product,
  ProductOption,
  ProductOptionConfig,
  ProductVariant,
  Provider,
  RecurringPayment,
  Reminder,
  Role,
  Shipment,
  ShippingCarrier,
  StripePriceModel,
  Subscription,
  SubscriptionExtensionPacks,
  Supplier,
  SupplierPayeeAccount,
  Transaction,
  User,
  UserCompanyMembership,
  CompanySettings,
  CommercialInvoice,
  CurrencyCodesType,
  PaymentStatus,
  Segment,
  ProductSegmentPricing,
  SubscriptionOptionsTypes,
  SubscriptionLevelEnum,
  SalesChannelType,
  ExternalApp,
  BulkPayment,
  IndividualPayment,
  UserEvent,
} from "@fena/toolkit-types";
import { SubscriptionTier } from "../modules/private/settings-module/billing-and-packages/shared/types";

export const PaymentProvidersCheckout =
  `${PaymentProviders.FENA_OB},${PaymentProviders.CARD}` as PaymentProviders;

export type WithId<T> = T & { _id: string };
export type UserApiType = WithId<
  Omit<User, "avatar"> & { avatar?: ImageAttachmentApiType }
>;
export type CompanyApiType = WithId<
  Omit<Company, "companySettings"> & { companySettings: CompanySettings }
>;
export type PaymentApiType = WithId<
  Omit<Payment, "refundPayeeAccount"> & {
    rawData?: string;
    refundPayeeAccount?: PayeeAccountApiType;
  }
>;
export type TransactionApiType = WithId<Transaction>;
export type CustomerApiType = WithId<
  Omit<Customer, "billingAddresses" | "deliveryAddresses"> & {
    billingAddresses: Array<
      Omit<CustomerAddress, "address"> & {
        address: AddressApiType;
      }
    >;
    deliveryAddresses: Array<
      Omit<CustomerAddress, "address"> & {
        address: AddressApiType;
      }
    >;
  }
>;
export type BrandApiType = WithId<Brand>;
export type CategoryApiType = WithId<Category>;
export type PickUpPointApiType = WithId<PickupPoint>;
export type ApiKeyApiType = WithId<ApiKey>;
export type RoleApiType = WithId<Role>;
export type BulkPaymentApiType = WithId<BulkPayment>;
export type IndividualPaymentApiType = WithId<IndividualPayment>;
export type OfficerApiType = WithId<CompanyOfficer>;
export type ProviderApiType = WithId<Provider>;
export type BankAccountApiType = WithId<BankAccount>;
export type SupplierApiType = WithId<Supplier>;
export type PayeeAccountApiType = WithId<PayeeAccount>;
export type ImageAttachmentApiType = WithId<ImageAttachment>;
export type NoteApiType = WithId<Note>;
export type AccountingSoftwareApiType = WithId<AccountingSoftware>;
export type ExternalAppApiType = WithId<ExternalApp>;
export type SaleChannelResult =
  | (ExternalIntegrationApiType & {
      channelType: SalesChannelType.EXTERNAL_INTEGRATION;
    })
  | (ExternalAppApiType & { channelType: SalesChannelType.EXTERNAL_APP });
export type CompanyOfficerApiType = WithId<
  Omit<CompanyOfficer, "company"> & { company: CompanyApiType }
>;
export type RecurringPaymentApiType = WithId<
  Omit<RecurringPayment, "bankAccount" | "transactions"> & {
    bankAccount?: Omit<BankAccountApiType, "provider"> & {
      provider?: ProviderApiType;
    };
    transactions?: TransactionApiType[];
  }
>;
export type FullUserCompanyMembership = Omit<
  UserCompanyMembership,
  "company" | "role" | "locations"
> & {
  company: CompanyApiType;
  role: RoleApiType;
  locations?: LocationApiType[];
};
export type ExternalIntegrationApiType = WithId<ExternalIntegration>;
export type AddressApiType = WithId<Address>;
export type SegmentApiType = WithId<
  Omit<Segment, "locations"> & { locations?: LocationApiType[] }
>;
export type SubscriptionExtensionPacksApiType =
  WithId<SubscriptionExtensionPacks> & { interval?: string };
export type SubscriptionApiType = WithId<Subscription>;
export type OrderApiType = WithId<
  Omit<
    Order,
    | "products"
    | "payment"
    | "customer"
    | "shippingAddress"
    | "billingAddress"
    | "notes"
    | "pickupPoint"
    | "shipments"
    | "picker"
    | "createdBy"
  > & {
    products: OrderProductApiType[];
    payment?: PaymentApiType;
    customer: CustomerApiType;
    shippingAddress?: AddressApiType | null;
    billingAddress?: AddressApiType | null;
    notes: NoteApiType[];
    pickupPoint?: PickUpPointApiType;
    shipments?: ShipmentApiType[];
    picker?: UserApiType;
    createdBy: any;
  }
>;
export interface B2bSubscriptionType {
  priceInSubunits: number;
  subscriptionLevel: SubscriptionLevelEnum;
  name: string;
  subscriptionOption: SubscriptionOptionsTypes.SHOPIFY_B2B_SUBSCRIPTION;
  active: boolean;
  isProcessing: boolean;
  pricing: {
    price: number;
    interval: string;
  };
  limits: {
    productsLimit: number;
    integrationsLimit: number;
    usersLimit: number;
    paymentsLimit: number;
    ordersLimit: number;
  };
}

export type ManagedCompaniesApiType = WithId<CompanyFullData>;
export type ManagedCompanyFullDataApiType = WithId<CompanyFullData>;
export type ManagedCompanyIntegrationsApiType =
  WithId<ManagedCompanyIntegration>;

// product typing
export type ProductOptionApiType = WithId<ProductOption>;
export type ProductOptionConfigReference = Omit<
  ProductOptionConfig,
  "option"
> & { option: ProductOptionApiType };

export type ProductVariantApiType = WithId<
  Omit<ProductVariant, "productOptionConfig" | "images"> & {
    productOptionConfig: ProductOptionConfigReference[];
    images?: ImageAttachmentApiType[];
  }
>;

export type ProductVariantWithParentProduct = WithId<
  Omit<ProductVariant, "productOptionConfig" | "images" | "parentProduct"> & {
    productOptionConfig: ProductOptionConfigReference[];
    images?: ImageAttachmentApiType[];
    parentProduct: ProductApiType;
  }
>;

export type ProductApiType = WithId<
  Omit<
    Product,
    "variants" | "options" | "brand" | "supplier" | "categories"
  > & {
    variants: ProductVariantApiType[];
    options: ProductOptionApiType[];
    brand?: BrandApiType;
    supplier?: SupplierApiType;
    categories?: CategoryApiType[];
  }
>;

export interface CalcVariantState {
  productVariant: ProductVariantApiType;
  currentCurrency: CurrencyCodesType;
  qty: number;
}

export type ProductTypeForTables = WithId<
  Omit<
    Product,
    "variants" | "options" | "brand" | "supplier" | "categories"
  > & {
    subRows?: ProductVariantWithParentProduct[];
    defaultVariant?: ProductVariantWithParentProduct;
    options: ProductOptionApiType[];
    brand?: BrandApiType;
    supplier?: SupplierApiType;
    categories?: CategoryApiType[];
  }
>;

export type OrderProductState = Partial<
  Omit<OrderProduct, "productVariant" | "qty">
> &
  Required<Pick<OrderProduct, "qty">> & {
    productVariant: ProductVariantApiType | ProductVariantWithParentProduct;
    currentCurrency: CurrencyCodesType;
    product?: ProductApiType;
    _id?: string;
  };

export type OrderProductApiType = WithId<
  Omit<OrderProduct, "productVariant" | "product"> & {
    productVariant: ProductVariantApiType;
    product: ProductApiType;
  }
>;
// End product typing

export interface SubscriptionApiTypeWithModifiedPrices
  extends Omit<SubscriptionApiType, "prices"> {
  prices: Array<
    StripePriceModel & {
      tiers?: SubscriptionTier[];
    }
  >;
}
export interface ToolkitSubscriptionWithModifiedPricesApiType
  extends Omit<SubscriptionApiType, "prices"> {
  prices: StripePriceModel | undefined;
}

export type CompanyWithAddressApiType = Omit<
  CompanyApiType,
  "address" | "tradingAddress"
> & {
  address: Address;
  tradingAddress: Address;
};

export type CompanyMembershipFullApiType = Omit<
  UserCompanyMembership,
  "company" | "role"
> & {
  company: CompanyWithAddressApiType;
  role: RoleApiType;
};
export type UserWithSelectedMembership = UserApiType & {
  selectedCompanyMembership: CompanyMembershipFullApiType | null;
};
export type UserWithCompanyMemberships = UserApiType & {
  companyMemberships: CompanyMembershipFullApiType[];
};
export type InvoiceApiType = WithId<
  Omit<Invoice, "reminders"> & {
    reminders: Array<
      Omit<InvoiceReminder, "customTriggerType"> & {
        customTriggerType?: string;
      }
    >;
  }
>;
export type CommercialInvoiceApiType = WithId<CommercialInvoice>;
export type ReminderApiType = WithId<Reminder>;
export type ShippingCarrierApiType = WithId<ShippingCarrier>;
export type LocationApiType = WithId<
  Omit<Location, "users"> & { users: TeamMemberType[] }
>;

export type ShipmentApiType = WithId<
  Omit<Shipment, "carrier"> & { carrier?: ShippingCarrierApiType }
>;
export type InventoryOrderApiType = WithId<
  Omit<InventoryOrder, "products"> & {
    products: Array<
      WithId<
        Omit<InventoryOrderProduct, "product" | "productVariant"> & {
          product: ProductApiType;
          productVariant: ProductVariantApiType;
        }
      >
    >;
  }
>;

export type FullRecurringPaymentApiType = WithId<
  Omit<RecurringPayment, "owner" | "initialPayment" | "transactions"> & {
    transactions: Transaction[];
    owner: Company;
    bankAccount: Omit<BankAccount, "provider"> & { provider: Provider };
    initialPayment: Omit<Payment, "transaction"> & {
      transaction: Transaction;
    };
  }
>;

export type ExtendedRoleApiType = RoleApiType & {
  users: UserApiType[];
  apiKeys: ApiKeyApiType[];
};

export type RestProps = Record<string, any>;

export type FullCompanyApiType = CompanyApiType & {
  legalFirstName?: string;
  legalLastName?: string;
};

type SupplierPayeeAccountBody = Omit<SupplierPayeeAccount, "payeeAccount"> & {
  payeeAccount: Partial<PayeeAccount>;
};
export type CreateSupplierBody = Partial<Supplier> & {
  address?: Partial<Address>;
  supplierPayeeAccount?: Partial<SupplierPayeeAccountBody>;
  deleteFile?: "true" | "false";
  // contact: { firstName: string; lastName: string };
};

export type CustomerCreateBody = Partial<Customer> & {
  billingAddress?: Omit<CustomerAddress, "address"> & {
    address: Partial<Address>;
  };

  deliveryAddress?: Omit<CustomerAddress, "address"> & {
    address: Partial<Address>;
  };
  deleteFile?: "true" | "false";
};

export type CustomerAddressBody = Omit<CustomerAddress, "address"> & {
  address: Partial<Address>;
};

export interface OrderProductBody {
  productId: string;
  productVariantId: string;
  qty: number;
  // custom values
  priceExcVat?: string;
  VAT?: number;
  discountRate?: number;
  hsCode?: string;
}
export interface CreateInvoiceBody {
  reference: string;
  /** Arrays of product  ids and qty */
  products: OrderProductBody[];
  /** Customer id */
  customer: string;
  /** Reminders ids */
  reminders?: InvoiceReminder[];
  customFields?: EntityCustomField[];
  customerEmailCC?: string[];
  dueBy?: Date;
  /** Note text */
  notes: Array<{
    text?: string;
    noteFor?: PersonEntityType[];
  }>;
  noteFor?: PersonEntityType[];
  deliveryAddress?: string;
  billingAddress?: string;
  discount?: number;
  shippingCost?: string;
  bankAccount: string;
  discountAmount?: string;
  issueDate?: Date;
  currency?: string;
}

export type InvoiceFullData = InvoiceApiType & {
  order: Omit<
    OrderApiType,
    "payment" | "products" | "notes" | "bankAccount" | "location"
  > & {
    payment: PaymentApiType;
    products: OrderProductApiType[];
    notes: NoteApiType[];
    bankAccount: BankAccountApiType;
    location: LocationApiType;
  };
  customer?: CustomerApiType;
  documentAttachments: DocumentAttachment[];
  bankAccount: BankAccountApiType;
  notes: NoteApiType[];
};

export type CreateOrderBody = Omit<Partial<Order>, "products"> & {
  /** Customer  data for right away creation */
  customerName?: string;
  customerEmail?: string;

  // array of products
  products: OrderProductBody[];
};

export enum CouriersTypes {
  ROYAL_MAIL_COURIER = "royal-mail",
  DPD_COURIER = "dpd",
  EVRI_COURIER = "evri",
  YODEL_COURIER = "yodel",
  DHL_COURIER = "dhl",
  PARCEL_FORCE_COURIER = "parcelforce",
  FEDEX_COURIER = "fedex",
  TNT_COURIER = "tnt",
  INPOST_COURIER = "inpost",
}

export interface MarkAsPaidInvoiceBody {
  paymentProvider: PaymentProviders;
  /** Cash / other payment completed date */
  completedAt?: Date;
  reference?: string;
}

export interface ProcessOrderBody {
  paymentDetails: {
    paymentProvider: PaymentProviders;
    bankAccount?: string;
    /** payment status for cash/card/other payments */
    paymentStatus?: PaymentStatus.SENT | PaymentStatus.PAID;
    /** payment completed date for cash/card/other PAID payments  */
    completedAt?: Date;
  };
  customerName?: string;
  customerEmail?: string;
  sendEmail?: boolean;
}

export interface ShipEnginePackage {
  weight: Weight;
  dimensions?: Dimensions;
}

export interface Weight {
  value: number;
  unit: string;
}
export type WeightUnit = "pound" | "ounce" | "gram" | "kilogram";

export interface Dimensions {
  unit: string;
  length: number;
  width: number;
  height: number;
}

export type DimensionUnit = "inch" | "centimeter";

export type TeamMemberType = Omit<
  UserApiType,
  "companyMemberships" | "avatar"
> & {
  companyMembership: Omit<UserCompanyMembership, "role"> & {
    role: RoleApiType;
  };
  avatar?: ImageAttachmentApiType;
};

export enum LimitTypesEnum {
  USERS = "USERS",
  PRODUCTS = "PRODUCTS",
  INTEGRATIONS = "INTEGRATIONS",
  PAYMENTS = "PAYMENTS",
}

export type ExternalIntegrationWithApiData = ExternalIntegrationApiType & {
  integrationApiData?: any | null;
};

// CI - commercial invoice
export interface CINonMandatoryFields {
  id: string;
  modeOfTransport?: string;
  route?: string;
  countryOfOrigin?: string;
}

export type PdfResponseFormat = "file" | "html" | "base64";

export interface DisputeInterface {
  amount: string;
  createdAt: number;
  currency: string;
  evidence: {
    customerEmailAddress: string;
    customerName: string;
  };
  reason: string;
  status: string;
}

export enum USER_ADDON_LIST_TYPES {
  SALES_AGENT = "SALES_AGENT",
  ACCOUNT_MONTHLY_FEE = "ACCOUNT_MONTHLY_FEE",
}
export interface CreateStripeUserAddonQuery {
  type?: USER_ADDON_LIST_TYPES;
}

export type FullProductSegmentPricing = Omit<
  ProductSegmentPricing,
  "segment"
> & { segment: SegmentApiType };

// React-query types
export interface PaginatedResponse<T> {
  docs: T[];
  totalDocs: number;
  hasNextPage?: boolean;
}

export type LastLoginData = UserEvent & {
  createdAt: string;
};

export interface FullTeamMembersResponse {
  result: {
    docs: TeamMemberType[];
    totalDocs: number;
  };

  lastLoginData: LastLoginData[];
}

export interface UseQueryErrorHandlerProps {
  isError: boolean;
  errorMessage?: string | null;
  handlerId?: string;
}

export enum QueryKeys {
  // 'List/Paginated list' request keys
  LIST_EXTERNAL_APP = "externalAppList",
  LIST_EXTERNAL_INTEGRATIONS = "externalIntegrationsList",
  LIST_API_KEYS = "apiKeysList",
  LIST_COMMERCIAL_INVOICES = "commercialInvoicesList",
  LIST_BANK_ACCOUNTS = "bankAccountsList",
  LIST_B2B_SUBSCRIPTIONS = "b2bSubscriptionsList",
  LIST_SUBSCRIPTIONS = "subscriptionsList",
  LIST_BILLING_HISTORY = "billingHistoryList",
  LIST_BILLING_ADDONS_HISTORY = "billingAddonsHistoryList",
  LIST_BILLING_PAYMENT_METHODS = "billingPaymentMethodsList",
  LIST_BILLING_SALES_AGENTS = "billingSalesAgentsList",
  LIST_BILLING_SUBSCRIPTION_ADDS_ON = "billingSubAddsOnList",
  LIST_BILLING_SUBSCRIPTION_STATS = "billingSubStatsList",
  LIST_MCC = "mccList",
  LIST_REGIONS = "regionsList",
  LIST_LOCATIONS = "locationsList",
  LIST_REMINDERS = "remindersList",
  LIST_ROLES = "rolesList",
  LIST_SEGMENTS = "segmentsList",
  LIST_TEAM_MEMBERS = "teamMembersList",
  LIST_COMPANY_OFFICERS = "companyOffersList",
  LIST_ACCOUNTING_INTEGRATIONS = "accountingIntegrationsList",
  LIST_HISTORY_ACTIONS = "historyActionsList",
  LIST_SALES_CHANNEL = "salesChannelsList",
  LIST_PAYOUTS = "payoutsList",
  LIST_DISPUTES = "disputesList",
  LIST_ORDERS = "ordersList",
  LIST_BRANDS = "brandsList",
  LIST_CATEGORIES = "categoriesList",
  LIST_SUPPLIERS = "suppliersList",
  LIST_CUSTOMERS = "customersList",
  LIST_PAYMENTS = "paymentsList",
  LIST_RECURRING_PAYMENT_TRANSACTIONS = "recurringPaymentTransactionsList",
  LIST_TRANSACTIONS = "transactionsList",
  LIST_PAYMENT_RUNS = "paymentRunsList",
  LIST_UPCOMING_PAYMENTS = "upcomingPaymentsList",
  LIST_PAYMENT_RUN_INDIVIDUAL_PAYMENTS = "paymentRunIndividualPaymentsList",
  LIST_INVOICES = "invoicesList",
  LIST_QUOTES = "quotesList",
  LIST_PURCHASE_ORDERS = "purchaseOrdersList",
  LIST_CARRIERS = "carriersList",
  LIST_PRODUCT_VARIANTS = "productVariantsList",
  LIST_PICKUP_POINTS = "pickupPointsList",
  LIST_PARTNER_MERCHANTS = "merchantsList",
  LIST_PARTNER_MERCHANT_TEAM_MEMBERS = "merchantTeamMembersList",
  LIST_PARTNER_TRANSACTIONS = "partnerTransactionsList",
  LIST_MERCHANTS_TRANSACTIONS_BY_SIZE = "merchantsTransactionsBySizeList",
  LIST_MANAGED_COMPANY_INTEGRATIONS = "managedCompanyIntegrationsList",
  LIST_PROVIDERS = "providersList",
  LIST_PRODUCTS = "productList",
  // End 'List/Paginated list' keys

  // 'Get' request keys
  GET_EXTERNAL_APP = "externalAppGet",
  GET_RESYNC_EXTERNAL_APP_CURRENCY = "resyncExternalAppCurrencyGet",
  GET_RESYNC_STORE_CURRENCY = "resyncStoreCurrencyGet",
  GET_EXTERNAL_INTEGRATION = "externalInternationGet",
  GET_API_KEY = "apiKeyGet",
  GET_MY_SUBSCRIPTION = "mySubscriptionGet",
  GET_DOWNLOAD_EXTERNAL_PLUGIN = "downloadExternalPluginGet",
  GET_LOCATION = "locationGet",
  GET_LOCATION_ASSOCIATIONS_COUNT = "locationAssociationsCountGet",
  GET_MARK_AS_DEFAULT_LOCATION = "getMarkAsDefaultLocation",
  GET_REMINDER = "getReminder",
  GET_EXPORT_SEGMENT_PRICING_CSV = "getExportSegmentPricingCsv",
  GET_TEAM_MEMBER = "getTeamMember",
  GET_USER = "getUser",
  GET_SUPPLIER = "getSupplier",
  GET_CUSTOMER = "getCustomer",
  GET_RECURRING_PAYMENT = "getRecurringPayment",
  GET_PAYMENT_RUN_BALANCE = "getPaymentRunBalance",
  GET_SINGLE_PAYMENT_RUN = "getSinglePaymentRun",
  GET_INVOICE = "getInvoice",
  GET_PAYMENT = "getPayment",
  GET_ORDER_DETAILS = "getOrderDetails",
  GET_COMMERCIAL_INVOICE = "getCommercialInvoice",
  GET_PRODUCT = "getProduct",
  GET_MERCHANTS_STATS = "getMerchantStats",
  GET_INFO_MERCHANTS_TOP_BOTTOM = "getIngoMerchantsTopBottom",
  // End 'Get' request keys

  // 'Create/Connect/Add' request keys
  CREATE_API_KEY = "createApiKey",
  CREATE_LOCATION = "createLocation",
  CREATE_REMINDER = "createReminder",
  CREATE_ROLE = "createRole",
  CREATE_SEGMENT = "createSegment",
  CREATE_TEAM_MEMBER = "createTeamMember",
  CREATE_CATEGORY = "createCategory",
  CREATE_SUPPLIER = "createSupplier",
  CREATE_CUSTOMER = "createCustomer",
  CREATE_DRAFT_PAYMENT = "createDraftPayment",
  CREATE_MERCHANT = "createMerchant",
  CONNECT_INTEGRATION = "connectIntegration",
  ADD_OFFICER = "addOfficer",
  ADD_CUSTOMER_ADDRESS = "addCustomerAddress",
  // End 'Create/Connect' request keys

  // 'Edit/Update/Post' request keys
  EDIT_API_KEY = "editApiKey",
  EDIT_EXTERNAL_APP = "editExternalApp",
  EDIT_EXTERNAL_INTEGRATION = "editExternalIntegration",
  EDIT_LOCATION = "editLocation",
  EDIT_REMINDER = "editReminder",
  EDIT_ROLE = "editRole",
  EDIT_CATEGORY = "editCategory",
  EDIT_DRAFT_PAYMENT = "editDraftPayment",
  UPDATE_LOCATION_FLAG = "updateLocationFlag",
  UPDATE_SEGMENT = "updateSegment",
  UPDATE_TEAM_MEMBER = "updateTeamMember",
  UPDATE_CUSTOMER = "updateCustomer",
  UPDATE_B2B_STORE_ACCESS = "updateB2bStoreStatus",
  UPDATE_SUPPLIER = "updateSupplier",
  UPDATE_PRODUCT_OPTIONS = "updateProductOptions",
  POST_SEND_TEST_REMINDER = "sendTestReminder",
  POST_BULK_ACTIVATE_TEAM_MEMBERS = "postBulkActivateTeamMembers",
  POST_BULK_SUSPEND_TEAM_MEMBERS = "postBulkSuspendTeamMembers",
  POST_BULK_DELETE_TEAM_MEMBERS = "postBulkDeleteTeamMembers",
  UPDATE_CUSTOMER_ADDRESS = "updateCustomerAddress",
  POST_SETUP_WHOLESALE_TAGS = "postSetupWholesaleTags",
  // End 'Edit/Update/Post' request keys

  // 'Delete/Remove/Archive' request keys
  DELETE_API_KEY = "deleteApiKey",
  DELETE_EXTERNAL_APP = "deleteExternalApp",
  DELETE_EXTERNAL_INTEGRATION = "deleteExternalIntegration",
  DELETE_LOCATION = "deleteLocation",
  DELETE_REMINDER = "deleteReminder",
  DELETE_OFFICER = "deleteOfficer",
  DELETE_CATEGORY = "deleteCategory",
  DELETE_SUPPLIER = "deleteSupplier",
  DELETE_INVOICE = "deleteInvoice",
  DELETE_QUOTE = "deleteQuote",
  DELETE_PICKUP_POINT = "deletePickupPoint",
  REPLACE_AND_DELETE_ROLE = "replaceAndDeleteRole",
  BULK_DELETE_SEGMENTS = "bulkDeleteSegments",
  BULK_DELETE_SUPPLIERS = "bulkDeleteSuppliers",
  BULK_DELETE_CUSTOMERS = "bulkDeleteCustomers",
  ARCHIVE_INVOICE = "archiveInvoice",
  ARCHIVE_QUOTE = "archiveQuote",
  REMOVE_TAG_FROM_ENTITY = "removeTagFromEntity",
  // End 'Delete/Remove/Archive' request keys

  SEND_DIRECTOR_INVITE = "sendDirectorInvite",
  CANCEL_INVOICE = "cancelInvoice",

  // Advanced search store keys
  ADVANCED_SEARCH_CATEGORIES_LIST = "advancedSearchCategoriesList",
  ADVANCED_SEARCH_CARRIERS_LIST = "advancedSearchCarriersList",
  ADVANCED_SEARCH_BRANDS_LIST = "advancedSearchBrandsList",
  ADVANCED_SEARCH_SUPPLIERS_LIST = "advancedSearchSuppliersList",
  ADVANCED_SEARCH_ROLES_LIST = "advancedSearchRolesList",
  ADVANCED_SEARCH_TEAM_MEMBERS_LIST = "advancedSearchPickersList",
  ADVANCED_SEARCH_LOCATIONS_LIST = "advancedSearchLocationsList",
  ADVANCED_SEARCH_API_KEYS_LIST = "advancedSearchApiKeysList",
  ADVANCED_SEARCH_INTEGRATIONS_LIST = "advancedSearchIntegrationsList",
  ADVANCED_SEARCH_EXTERNAL_APPS_LIST = "advancedSearchExternalAppsList",
  ADVANCED_SEARCH_ACCOUNTING_INTEGRATIONS_LIST = "advancedSearchAccountingIntegrationsList",
  ADVANCED_SEARCH_PARTNER_MERCHANTS_LIST = "advancedSearchAccountingIntegrationsList",
  ADVANCED_SEARCH_ORDER_TAGS_LIST = "advancedSearchOrderTagsList",
  ADVANCED_SEARCH_CUSTOMER_TAGS_LIST = "advancedSearchCustomerTagsList",
  // End Advanced search store keys
}
// End React-query types
